import { useMemo } from "react"
import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import {
  PaginationState,
  RowSelectionState,
  OnChangeFn,
} from "@tanstack/react-table"
import type { HomepageFunnelTableRow } from "../connect"
import { Loader } from "~/ui-rtk/components/ui/common/Loader"

const formatPercent = (value: number | undefined | null) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "--"
  }
  return `${value.toFixed(1)}%`
}

export const ALL_COLUMNS: TableVisualizationColumn[] = [
  {
    key: "deviceCategory",
    header: "Device Category",
    type: "text",
    width: 150,
    render: (row: TMetric) => {
      const typedRow = row as unknown as HomepageFunnelTableRow
      return <span className="font-medium">{typedRow.deviceCategory}</span>
    },
  },
  {
    key: "homepageViewers",
    header: "Homepage Viewers",
    type: "number",
    width: 150,
  },
  {
    key: "shoppingPageViewers",
    header: "Shopping Page Viewers",
    type: "number",
    width: 180,
  },
  {
    key: "addersToCart",
    header: "Adders to Cart",
    type: "number",
    width: 150,
  },
  {
    key: "purchasers",
    header: "Purchasers",
    type: "number",
    width: 120,
  },
  {
    key: "homepageToShopConversion",
    header: "Homepage → Shop Rate",
    type: "number",
    width: 180,
    render: (row: TMetric) => {
      const typedRow = row as unknown as HomepageFunnelTableRow
      return (
        <div>
          <div className="font-semibold text-white">
            {formatPercent(typedRow.homepageToShopConversion)}
          </div>
          <div className="text-sm text-system-gray-400">
            Abandon: {formatPercent(100 - typedRow.homepageToShopConversion)}
          </div>
        </div>
      )
    },
  },
  {
    key: "shopToCartConversion",
    header: "Shop → Cart Rate",
    type: "number",
    width: 180,
    render: (row: TMetric) => {
      const typedRow = row as unknown as HomepageFunnelTableRow
      return (
        <div>
          <div className="font-semibold text-white">
            {formatPercent(typedRow.shopToCartConversion)}
          </div>
          <div className="text-sm text-system-gray-400">
            Abandon: {formatPercent(100 - typedRow.shopToCartConversion)}
          </div>
        </div>
      )
    },
  },
  {
    key: "homepageToCartConversion",
    header: "Homepage → Cart Rate",
    type: "number",
    width: 180,
    render: (row: TMetric) => {
      const typedRow = row as unknown as HomepageFunnelTableRow
      return (
        <div>
          <div className="font-semibold text-white">
            {formatPercent(typedRow.homepageToCartConversion)}
          </div>
          <div className="text-sm text-system-gray-400">
            Abandon: {formatPercent(100 - typedRow.homepageToCartConversion)}
          </div>
        </div>
      )
    },
  },
  {
    key: "cartToPurchaseConversion",
    header: "Cart → Purchase Rate",
    type: "number",
    width: 180,
    render: (row: TMetric) => {
      const typedRow = row as unknown as HomepageFunnelTableRow
      return (
        <div>
          <div className="font-semibold text-white">
            {formatPercent(typedRow.cartToPurchaseConversion)}
          </div>
          <div className="text-sm text-system-gray-400">
            Abandon: {formatPercent(100 - typedRow.cartToPurchaseConversion)}
          </div>
        </div>
      )
    },
  },
  {
    key: "shopToPurchaseConversion",
    header: "Shop → Purchase Rate",
    type: "number",
    width: 180,
    render: (row: TMetric) => {
      const typedRow = row as unknown as HomepageFunnelTableRow
      return (
        <div>
          <div className="font-semibold text-white">
            {formatPercent(typedRow.shopToPurchaseConversion)}
          </div>
          <div className="text-sm text-system-gray-400">
            Abandon: {formatPercent(100 - typedRow.shopToPurchaseConversion)}
          </div>
        </div>
      )
    },
  },
  {
    key: "homepageToPurchaseConversion",
    header: "Homepage → Purchase Rate",
    type: "number",
    width: 200,
    render: (row: TMetric) => {
      const typedRow = row as unknown as HomepageFunnelTableRow
      return (
        <div>
          <div className="font-semibold text-white">
            {formatPercent(typedRow.homepageToPurchaseConversion)}
          </div>
          <div className="text-sm text-system-gray-400">
            Abandon:{" "}
            {formatPercent(100 - typedRow.homepageToPurchaseConversion)}
          </div>
        </div>
      )
    },
  },
]

interface HomepageFunnelTableProps {
  data: HomepageFunnelTableRow[]
  compareData?: HomepageFunnelTableRow[]
  compareUniqueKey?: string
  isLoading: boolean
  paginationState: PaginationState
  setPaginationState: (
    state: PaginationState | ((prev: PaginationState) => PaginationState),
  ) => void
  totalCount: number
  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  columnConfig?: TableVisualizationColumn[]
  /** Export CSV button (rendered by TableVisualization when set). */
  csvExport?: () => Promise<void>
  isExporting?: boolean
  exportProgress?: { progress: number; message: string }
}

export default function HomepageFunnelTable({
  data,
  compareData,
  compareUniqueKey = "deviceCategory",
  isLoading,
  paginationState,
  setPaginationState,
  totalCount,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange,
  columnConfig: columnConfigProp,
  csvExport,
  isExporting,
  exportProgress,
}: HomepageFunnelTableProps) {
  // Convert data to metrics format
  const metrics = useMemo(() => {
    if (compareData && compareData.length > 0) {
      return [
        data.map(row => ({
          ...row,
          id: row.deviceCategory,
        })),
        compareData.map(row => ({
          ...row,
          id: row.deviceCategory,
        })),
      ]
    }
    return data.map(row => ({
      ...row,
      id: row.deviceCategory,
    }))
  }, [data, compareData])

  const columnConfig = useMemo<TableVisualizationColumn[]>(
    () => [
      ...(enableRowSelection
        ? [
            {
              key: "select",
              header: "",
              type: "text" as const,
              width: 50,
              disableSorting: true,
            },
          ]
        : []),
      ...(columnConfigProp ?? ALL_COLUMNS),
    ],
    [enableRowSelection, columnConfigProp],
  )

  if (!data.length && totalCount === 0 && !isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-system-gray-400">
        No data available for the selected filters.
      </div>
    )
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-basic-black/50 rounded-system-lg">
          <Loader size={50} />
        </div>
      )}
      <TableVisualization
        metrics={metrics as TMetric[]}
        columnConfig={columnConfig}
        paginationState={paginationState}
        setPaginationState={setPaginationState}
        enablePagination={true}
        chartId="homepage-funnel-table"
        manualPagination={true}
        totalPages={Math.ceil(totalCount / paginationState.pageSize)}
        compareMode={Boolean(compareData && compareData.length > 0)}
        compareUniqueKey={compareUniqueKey}
        enableRowSelection={enableRowSelection}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        csvExport={csvExport}
        isExporting={isExporting}
        exportProgress={exportProgress}
      />
    </div>
  )
}
